import { updateValuesWrapper } from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { PushProcessingNotification } from "@repo/helpers";
import { useCallback, useMemo } from "react";

import { useRepOnboardingQuery, useTabCountQuery } from "../../../../api/repApi";
import { useTrainingApi, useTrainingQueries } from "../../../../api/trainingApi";
import { EditableItemDynamicTab } from "../../../../components/dynamicEditor/dynamicTab/editableItem";
import { ITemplateData } from "../../../../components/dynamicEditor/templateDataHook";
import { useRepContext, useStateSpecificTrainingContext } from "../../../../contexts/editableItemContext";
import { MVCStateSpecificTraining } from "../../../../models/MVCStateSpecificTraining";
import { tabs } from "../../../../string";

interface IState extends ITemplateData {
	stateSpecificTraining: MVCStateSpecificTraining;
}

const COMMODITY_TYPE = [
	{ id: 1, value: "Electric" },
	{ id: 2, value: "Natural Gas" },
];

function useStateSpecificTrainingLists() {
	const { useStatesQuery } = useTrainingQueries();
	const { data } = useStatesQuery();

	const activeLists = useMemo(() => {
		return {
			states: data,
			commodityType: COMMODITY_TYPE,
		};
	}, [data]);

	return [activeLists];
}

function StateSpecificTrainingEditorDynamic() {
	const { repId } = useRepContext();
	const context = useStateSpecificTrainingContext();
	const { checkOnboarding } = useRepOnboardingQuery();
	const [trainingLists] = useStateSpecificTrainingLists();
	const { saveRepAdditionalTrainings, getStateSpecificTraining } = useTrainingApi();
	const { clearQuery: clearTabCount } = useTabCountQuery(repId);

	const stateSpecificTraining = context.item;
	const templateData = useMemo(
		() => ({
			stateSpecificTraining,
			trainingLists,
		}),
		[stateSpecificTraining, trainingLists],
	);

	const save = useCallback(async () => {
		const notify = PushProcessingNotification("Saving Training...");
		try {
			const id = await saveRepAdditionalTrainings(repId, stateSpecificTraining);
			notify.processing("Loading...");
			const newTraining = await getStateSpecificTraining(repId, id);
			notify.success("Saved!");
			await checkOnboarding();
			await clearTabCount();
			return newTraining;
		} catch (ex) {
			notify.error("Failed to save");
			throw ex;
		}
	}, [repId, stateSpecificTraining]);

	const updateValues = useCallback(updateValuesWrapper(applyBusinessLogic), [applyBusinessLogic]);

	async function applyBusinessLogic(id: string, value: any, newState: IState) {
		//Custom logic
		switch (id) {
			default:
				break;
		}

		return newState as IState;
	}

	return (
		<EditableItemDynamicTab
			itemContext={context}
			pageName={tabs.stateSpecificTraining}
			otherTemplateData={templateData}
			itemProp="stateSpecificTraining"
			updateValues={updateValues}
			save={save}
			loaded
		/>
	);
}

export default StateSpecificTrainingEditorDynamic;
